import { Store } from 'redux';
import * as _ from 'underscore';

import { ContentFilterKeyName, ContentFiltersKeys, ContentTypeFilterKey } from '../../../ts/models/ContentFilters';
import { getDefaultOrderForContentTypesOrSpaceInfo } from '../../../ts/models/sort/SortOptionDefinitions';
import { setOrderAction } from '../../actions/SetOrderAction';
import { contentTypeFilterSelected } from '../../actions/spaces/ContentExplorerFilterActions';
import { setContentFilterNoRequestContent } from '../../actions/spaces/filters/FilterActions';
import { selectCurrentlyApplicableContentTypes, selectDefaultContentTypeFilterValues } from '../../selectors/ContentFilterSelectors';
import { selectSpaceInfo } from '../../selectors/ContentSelectors';
import { AppState } from '../../state/AppState';
import { SITE_SPACE_EXPLORE, SiteSpaceContentType } from '../../state/SiteSpaceContentTypeState';
import { isFavoritesSpace, isMetricSpace, isProjectSpace } from '../../state/SpaceInfo';
import { UrlParamKeys } from '../../UrlParamKeys';
import { getContentTypesFromQueryParam, getOrderClausesFromQueryParam } from '../Explorer/ExplorerUrlBindings';

export namespace SiteSpaceConfigHelper {

  const UNUSED_SITE_SUFFIX = 'UNUSED';

  // In the Angular site place, the suffix is also used as the subplace name.
  const UrlSuffixForSiteSpaceContentType: Readonly<Record<SiteSpaceContentType, string>> = {
    explore: SITE_SPACE_EXPLORE,
    project: 'projects',
    workbook: 'workbooks',
    datasource: 'datasources',
    dataRole: 'dataRoles',
    dataconnection: UNUSED_SITE_SUFFIX, // we never allow navigating to data connections at top level
    flow: 'flows',
    view: 'views',
    database: 'catalog',
    table: 'catalog',
  };

  // Invert the map so we can translate from URL suffix to site space content type
  const SiteSpaceContentTypeForUrlSuffix: Readonly<_.Dictionary<SiteSpaceContentType>> = _.invert(UrlSuffixForSiteSpaceContentType);

  export function siteSpaceUrlSuffix(contentType?: SiteSpaceContentType): string {
    const suffix = contentType && UrlSuffixForSiteSpaceContentType[contentType];

    // For a content type unsupported at the site space, redirect to the default
    return suffix || '';
  }

  export function contentTypeFromCurrentSitePlaceState(currentState: ng.ui.IState): SiteSpaceContentType | '' {
    const suffix = (currentState.url as string).slice(1);
    const siteSpaceContentType = suffix && SiteSpaceContentTypeForUrlSuffix[suffix];
    return siteSpaceContentType || undefined;
  }

}

// For setting defaults
// TODO - The actions that we are using here not only set the default values but also calls the fetch contents. Refactor this to be cleaner,
// where this only sets the default parameters and than the the fetch content call is seperate which happens after all the default values are set.
export function setURLParametersOrDefaultsForEnteringSpaceAndFetchContent(
    store: Store<any>,
    $location: ng.ILocationService
) {
  // It is necessary that the calls below happen in this order as we want the filters and sort order to be set before fetching the contents
  // clearAndSetFiltersFromURL also makes call to fetch content after setting the filter
  clearAndSetFiltersFromURL(store, $location);
  clearAndSetSortOrderFromURL(store, $location);

  // this makes call to fetch the contents and the sort and filters should be set before this.
  clearAndSetContentTypeFromURL(store, $location);
}

export function clearAndSetFiltersFromURL(store: Store<any>, $location: ng.ILocationService) {
  for (const filterType of _.keys(ContentFiltersKeys)) {
    const value = $location.search()[filterType];
    store.dispatch(setContentFilterNoRequestContent(filterType as ContentFilterKeyName, value? value + '' : undefined));
  }
}

export function clearAndSetSortOrderFromURL(store: Store<any>, $location: ng.ILocationService) {
  const contentTypes = selectCurrentlyApplicableContentTypes(store.getState());
  const spaceInfo = selectSpaceInfo(store.getState());
  const defaultOrder = getDefaultOrderForContentTypesOrSpaceInfo(contentTypes, spaceInfo);

  const orderToSet = getOrderClausesFromQueryParam($location.search()[UrlParamKeys.Order], defaultOrder);
  store.dispatch(setOrderAction(orderToSet));
}

export function clearAndSetContentTypeFromURL(
    store: Store<AppState>,
    $location: ng.ILocationService,
) {
  const spaceInfo = selectSpaceInfo(store.getState());
  if (isProjectSpace(spaceInfo) ||
      isFavoritesSpace(spaceInfo) ||
      isMetricSpace(spaceInfo)) {
    let contentTypesParam = $location.search()[ContentTypeFilterKey];
    const defaultValues = selectDefaultContentTypeFilterValues(store.getState());

    // We tell the action to clearAndSet content filter types to ensure we don't do any 'optimizing' of content counts
    store.dispatch(contentTypeFilterSelected(getContentTypesFromQueryParam(contentTypesParam, defaultValues), true /*clearAndSet*/));
  }
}
